import {
  MouseEvent,
  ChangeEvent,
  FocusEvent,
  forwardRef,
  RefObject,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react"

import { v4 as uuid } from "uuid"

import { InputSize } from "../../enumerations"
import { FloatingLabel } from "../floating-label"
import { GenericInput } from "../input-base"

import { InputBaseStyled, InputMemoized } from "./styles"
import { TextFieldProps } from "./types"

// A normal value of an input element has `readonly string[]` rather than `string[]`
type HTMLInputElementValue = string | number | string[] | undefined

export const TextField = forwardRef<HTMLInputElement, TextFieldProps>(
  (
    {
      id,
      children,
      label,
      placeholder,
      svgIcon,
      customIcon,
      prefix,
      value,
      size = InputSize.Large,
      semanticState,
      onBlur,
      onFocus,
      onChange,
      // props that need to be passed to mask rather than child input
      onPaste,
      onMouseDown,
      disabled,
      isLoading,
      isLabelHidden,
      isErrorStateOverriden,
      readOnly,
      // rest of props to be passed to input
      ...props
    },
    ref,
  ) => {
    const [isFocused, setIsFocused] = useState(false)
    const [currentValue, setCurrentValue] = useState(value)
    const [isElevated, setIsElevated] = useState(!value && label ? true : Boolean(value))
    const previousValue = useRef<HTMLInputElementValue | null>(null)
    const localInputRef = useRef<HTMLInputElement>(null)
    const inputRef = (ref as RefObject<HTMLInputElement>) ?? localInputRef
    // eslint-disable-next-line @typescript-eslint/prefer-nullish-coalescing
    const textFieldId = useMemo(() => id || uuid(), [id])

    const handleMouseDown = useCallback((event: MouseEvent<HTMLInputElement>) => {
      const isRefCallback = typeof inputRef === "function"
      if (event.target !== inputRef.current && !isRefCallback) {
        event.preventDefault()
        inputRef.current?.focus()
      }
      onMouseDown?.(event)
    }, [])

    const handleChange = useCallback(
      (event: ChangeEvent<HTMLInputElement>) => {
        setCurrentValue(event.target.value)
        onChange?.(event)
      },
      [onChange],
    )

    const handleFocus = useCallback(
      (event: FocusEvent<HTMLInputElement>) => {
        setIsElevated(true)
        setIsFocused(true)
        onFocus?.(event)
      },
      [onFocus],
    )

    const handleBlur = useCallback(
      (event: FocusEvent<HTMLInputElement>) => {
        setIsElevated(Boolean(currentValue))
        setIsFocused(false)
        onBlur?.(event)
      },
      [currentValue, onBlur],
    )

    const shouldShowPlaceholder = Boolean(
      (label && (isFocused || isLabelHidden) && placeholder) || (!label && placeholder),
    )

    useEffect(() => {
      if (previousValue.current !== value) {
        setCurrentValue(value)
      }
      previousValue.current = value as HTMLInputElementValue
    }, [value])

    useEffect(() => {
      setIsElevated(Boolean(currentValue) || isFocused)
    }, [currentValue, isFocused])

    useEffect(() => {
      if (isElevated && isFocused) {
        inputRef.current?.focus()
      }
    }, [children])

    const isPrefixVisible = useMemo(() => {
      const isLabelPresent = Boolean(label)
      const isLabelPresentAndElevated = isLabelPresent && isElevated
      const isPrefixSupplied = Boolean(prefix)
      return (
        isPrefixSupplied && (!isLabelPresent || isLabelPresentAndElevated || isFocused)
      )
    }, [label, prefix, isFocused, isElevated])

    return (
      <InputBaseStyled
        id={textFieldId}
        isIdNotUsedDirectly
        isLoading={isLoading}
        className="otto-text-field-outer-container"
        isFocused={isFocused}
        isDisabled={disabled}
        isPrefixVisible={isPrefixVisible}
        svgIcon={svgIcon}
        semanticState={semanticState}
        customIcon={customIcon}
        prefix={prefix}
        size={size}
        isErrorStateOverriden={isErrorStateOverriden}
        onMouseDown={handleMouseDown}
      >
        {Boolean(label) && (
          <FloatingLabel
            id={`${textFieldId}-label`}
            isElevated={isElevated}
            isHidden={isLabelHidden}
            isWithIcon={Boolean(svgIcon ?? customIcon)}
            isErrorStateOverriden={isErrorStateOverriden}
            htmlFor={textFieldId}
          >
            {label}
          </FloatingLabel>
        )}
        <GenericInput>
          <InputMemoized
            ref={inputRef}
            {...props}
            id={textFieldId}
            placeholder={shouldShowPlaceholder ? placeholder : undefined}
            value={value ?? currentValue ?? ""}
            onChange={handleChange}
            onFocus={handleFocus}
            onBlur={handleBlur}
            onPaste={onPaste}
            onMouseDown={onMouseDown}
            disabled={disabled}
            readOnly={readOnly}
            aria-readonly={readOnly}
            aria-busy={isLoading}
            aria-labelledby={label ? `${textFieldId}-label` : undefined}
          />
        </GenericInput>
        {children}
      </InputBaseStyled>
    )
  },
)
